import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authStore } from '@/auth/authStore';
import { useManifest } from '@/meta/useManifest';

const PROVIDERS = [
  { id: 'yandex', label: 'Войти через Яндекс' },
  { id: 'vk', label: 'Войти через VK ID' },
] as const;

// Версия документа согласия: фолбэк, канон — манифест (consentVersion зеркалит
// Consent:CurrentVersion бэкенда); на логине сети может не быть — фолбэк обязателен
const DEFAULT_CONSENT_VERSION = '2026-06';
const CONSENT_KEY = 'dg_consent_ack';

// localStorage может отсутствовать (Safari private mode) — гейт тогда просто не персистится
function readAck(version: string): boolean {
  try { return localStorage.getItem(CONSENT_KEY) === version; } catch { return false; }
}
function writeAck(next: boolean, version: string): void {
  try {
    if (next) localStorage.setItem(CONSENT_KEY, version);
    else localStorage.removeItem(CONSENT_KEY);
  } catch { /* in-memory only */ }
}

export function LoginScreen() {
  const { manifest } = useManifest();
  const consentVersion = manifest?.consentVersion ?? DEFAULT_CONSENT_VERSION;
  const [agreed, setAgreed] = useState(() => readAck(DEFAULT_CONSENT_VERSION));
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // манифест приехал с другой версией документа → старый ack недействителен,
  // гейт пересчитывается от новой версии
  useEffect(() => { setAgreed(readAck(consentVersion)); }, [consentVersion]);

  function toggleConsent(next: boolean) {
    setAgreed(next);
    writeAck(next, consentVersion);
  }

  async function login(provider: string) {
    try {
      // mode=json: этим же запросом ставится dg_oauth-cookie, затем уходим к провайдеру
      const url = await authStore.getAuthorizeUrl(provider, '/');
      window.location.assign(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <main className="meta-screen" data-testid="screen-login">
      <header className="meta-subbar">
        <Link to="/" className="back-link" data-testid="back-link">← Назад</Link>
        <h1>Вход</h1>
      </header>
      <div className="meta-body">
        <div className="auth-card">
          <h2>Войти в игру</h2>
          <label className="consent-row">
            <input
              type="checkbox"
              data-testid="consent-checkbox"
              checked={agreed}
              onChange={(e) => toggleConsent(e.target.checked)}
            />
            <span>Согласен на обработку персональных данных</span>
          </label>
          {PROVIDERS.map((p) => (
            <button
              key={p.id}
              className="btn"
              data-testid={`login-${p.id}`}
              disabled={!agreed}
              onClick={() => void login(p.id)}
            >
              {p.label}
            </button>
          ))}
          <div className="auth-or">или</div>
          {/* гость играет без согласия и аккаунта: ПД не обрабатываем, сейв — localStorage */}
          <button
            className="btn btn-ghost"
            data-testid="login-guest"
            onClick={() => navigate('/map')}
          >
            Продолжить как гость
          </button>
          <p className="hint">
            Гостевой сейв живёт в этом браузере. Войдёшь позже — прогресс сольётся с аккаунтом.
          </p>
          {error && <output data-testid="login-error" className="error-box">Не получилось: {error}</output>}
        </div>
      </div>
    </main>
  );
}
